import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from 'react-intl';
import {
    Box,
    Menu,
    MenuItem,
    ListItemIcon,
    Divider,
    IconButton,
    Icon,
    Typography,
    Grid,
    ListItemText
} from "@mui/material";
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Button, Tooltip, Table } from "@gpstrackit/react-controls"
import { getDateFormatted, openLink } from 'Core/data/Helpers';
import NotificationBadge from "../../../../../components/NotificationBadge";

import {
    changePasswordOpenModal,
    setChangeSelectLanguage,
    searchNotificationsRequest,
    updateNotificationRequest,
    searchCountNotificationsRequest,
    logoutRequest,
    downloadReportFileRequest,
    removePendingExport,
    clearRedux,
} from 'Redux/actions';
import { resetAllEntities } from 'Core/common/redux/infiniteScrollSlice';

//Styles
import './menuAccount.scss';

const MenuAccount = (props) => {

    const dispatch = useDispatch();
    //Current User
    const { user } = useSelector(state => state.securityRedux);
    const { logoutResult } = useSelector(state => state.loginRedux);

    const { countPendingNotifications, notifications, notificationsNextToken, loadingNotifications, updateNotificationResult, loadingUpdateNotification } = useSelector(state => state.generalRedux);
    const downloadReportFileResult = useSelector(state => state.generalRedux.downloadReportFileResult);
    const pendingExports = useSelector(state => state.generalRedux.pendingExports);

    // Tracks which export just had its link copied (shows "Copied" briefly)
    const [copiedId, setCopiedId] = useState(null);

    // Live elapsed time ticker — re-renders every second while pendingExports has items
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!pendingExports || pendingExports.length === 0) return;
        const timer = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(timer);
    }, [pendingExports?.length]);

    const formatElapsed = (startedAt) => {
        const secs = Math.floor((Date.now() - startedAt) / 1000);
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return m > 0 ? `${m}m ${s}s` : `${s}s`;
    };

    const EXPORT_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
    const EXPORT_COMPLETE_AUTO_REMOVE_MS = 5 * 60 * 1000; // 5 minutes after complete

    // Auto-remove completed/expired exports after 5 minutes
    useEffect(() => {
        if (!pendingExports || pendingExports.length === 0) return;
        const timer = setInterval(() => {
            const now = Date.now();
            pendingExports.forEach(exp => {
                const isExpired = exp.status === 'pending' && (now - exp.startedAt) >= EXPORT_TIMEOUT_MS;
                const isStaleComplete = exp.status === 'complete' && exp.completedAt && (now - exp.completedAt) >= EXPORT_COMPLETE_AUTO_REMOVE_MS;
                if (isExpired || isStaleComplete) {
                    dispatch(removePendingExport(exp.id));
                }
            });
        }, 15000); // check every 15s
        return () => clearInterval(timer);
    }, [pendingExports]);

    const navigate = useNavigate();

    const { messages } = useIntl();
    const tableRef = useRef(null);
    const safariTabRef = useRef(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [anchorElNotifications, setAnchorElNotifications] = useState(null);

    const [selectLan, setSelectLan] = useState();
    const open = Boolean(anchorEl);
    const openNotifications = Boolean(anchorElNotifications);

    const handleClick = useCallback((event) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
    }, [anchorEl]);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [anchorEl]);

    const handleClickNotifications = useCallback((event) => {
        event.preventDefault();
        setAnchorElNotifications(event.currentTarget);
    }, [anchorElNotifications]);

    const handleCloseNotifications = useCallback(() => {
        setAnchorElNotifications(null);
    }, [anchorElNotifications]);

    const logout = useCallback(() => {
        navigate(`/login`);
        localStorage.clear();
        dispatch(clearRedux(""))
        dispatch(resetAllEntities())
    });

    const onChangeLan = () => {
        dispatch(setChangeSelectLanguage(selectLan.id))
        localStorage.setItem('lan', selectLan.id)
        switchChangeLan(selectLan.id)
    }

    //function use switch for language change
    const switchChangeLan = (lanState) => {
        switch (lanState) {
            case 'es':
                return setSelectLan({ id: 'en', label: 'English' })
            case 'en':
                return setSelectLan({ id: 'es', label: 'Español' })
            default:
                return setSelectLan({ id: 'en', label: 'English' })
        }
    }


    //funtion to get the notification title according to the type of the notification
    const getNotification = (notification) => {
        let content = JSON.parse(notification.payload)
        switch (notification.type) {
            case "exporter":
                let data = content.data.url[0].url.split("exported-files/");
                data = data[1].split("_");
                let entity = data[0]

                return (<Grid item xs={8}>
                    <Grid item xs={12}>
                        <Tooltip title={messages["menu_account_notifications_export_message"]}>
                            <span className="menu-content-notification-title">
                                {`${messages["menu_account_notifications_export"]} ${messages[entity] || entity}`}
                            </span>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                        <span className="menu-content-notification-subtitle">
                            {getDateFormatted(notification.createdOn, "date", "datetime")}
                        </span>
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: '8px' }}>
                        <Button
                            className="bg-primary text-white"
                            variant="contained"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                // Safari fix: Open synchronous tab before async request
                                safariTabRef.current = window.open('about:blank', '_blank');
                                if (safariTabRef.current) {
                                    safariTabRef.current.document.write('Downloading...');
                                }
                                const url = content.data.url[0].url.split('?')[0] || '';
                                dispatch(downloadReportFileRequest({ id: url }));
                                dispatch(updateNotificationRequest(notification.id));
                            }}
                        >
                            {messages["common_download"] || "Download"}
                        </Button>
                    </Grid>
                </Grid>)
                break;

            default:
                break;
        }

    }

    useEffect(() => {
        const language = localStorage.getItem('lan') || "en"
        switchChangeLan(language)
    }, [])

    useEffect(() => {
        if (updateNotificationResult) {
            dispatch(clearRedux("GENERAL", { updateNotificationResult: null, notifications: [], notificationsNextToken: null }));
            dispatch(searchCountNotificationsRequest());
        }
    }, [updateNotificationResult])

    useEffect(() => {
        if (logoutResult) {
            logout();
        }
    }, [logoutResult]);

    useEffect(() => {
        if (downloadReportFileResult && downloadReportFileResult?.url) {
            if (safariTabRef.current) {
                safariTabRef.current.location.href = downloadReportFileResult.url;
                safariTabRef.current = null;
            } else {
                openLink(downloadReportFileResult?.url)
            }
            dispatch(clearRedux("GENERAL", { downloadReportFileResult: null }))
        }
    }, [downloadReportFileResult])

    return <>
        <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
            <NotificationBadge
                width={"25px"}
                color={"#123C34"}
                count={(countPendingNotifications || 0) + (pendingExports?.filter(e => e.status !== 'complete').length || 0)}
                onClick={(e) => {
                    handleClickNotifications(e)
                    dispatch(searchNotificationsRequest(10, null));
                    dispatch(clearRedux("GENERAL", { notifications: [], notificationsNextToken: null }))
                }}>
                <NotificationsIcon />
            </NotificationBadge>
            <IconButton
                onClick={(e) => handleClick(e)}
                aria-controls={open ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                size="large">
                <Icon style={{ color: 'black' }}>person</Icon>
            </IconButton>
        </Box>

        <Menu
            style={{ width: 280 }}
            elevation={5}
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
                elevation: 0,
                sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1,
                    },
                    '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                    },
                },
            }}
        >
            <MenuItem style={{ padding: 0 }}>
                <Grid container>
                    <Grid style={{ padding: '10px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} >
                        <div style={{ width: 120 }}>
                            {<Tooltip title={user?.fullname || ''}>
                                <Typography
                                    style={{ textTransform: 'capitalize', width: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                    variant="h6"
                                    component="h6"
                                >
                                    {user?.fullname || ''}
                                </Typography>
                            </Tooltip>}

                        </div>
                        <Button
                            className="btn-dark-blue"
                            type="button"
                            onClick={(e) => {
                                onChangeLan();
                            }}
                        >
                            {selectLan?.label}
                        </Button>
                    </Grid>
                </Grid>
            </MenuItem>
            <MenuItem
                style={{ paddingLeft: 16, paddingRight: 16, width: '100%', height: 36 }}
                onClick={(e) => {
                    e.preventDefault();
                    dispatch(changePasswordOpenModal(true))
                }}
            >
                <ListItemText>{messages['menu_account_change_password']}</ListItemText>
            </MenuItem>
            <MenuItem
                style={{ paddingLeft: 16, paddingRight: 16, width: '100%', height: 36 }}
                onClick={(e) => {
                    localStorage.removeItem("general");
                    localStorage.removeItem("preferencesOther");
                    location.reload();
                }}
            >
                <ListItemText>{messages['menu_account_clear_caches']}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={(e) => {
                e.preventDefault()
                dispatch(logoutRequest());
            }} style={{ paddingLeft: 16, paddingRight: 16, width: '100%' }}>
                <ListItemIcon>
                    <Icon>logout</Icon>
                </ListItemIcon>
                <ListItemText>{messages['menu_account_logout']}</ListItemText>
            </MenuItem>
        </Menu >
        <Menu
            elevation={5}
            anchorEl={anchorElNotifications}
            getcontentanchorel={null}
            id="notifications-menu"
            open={openNotifications}
            onClose={handleCloseNotifications}
            onClick={handleCloseNotifications}
            PaperProps={{
                elevation: 2,
                sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                    },
                    '&.MuiMenu-paper': {
                        color: 'red'
                    }
                },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
            <Grid container>
                <Grid style={{ width: 300, padding: '10px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box
                        sx={{
                            width: '100%',
                            '& .MuiTable-root': {
                                border: '2px solid #e0e0e0',
                                borderBottom: '2px solid #e0e0e0',
                            },
                        }}
                    >
                    {/* ---- Pending / completed export trackers ---- */}
                    {pendingExports && pendingExports.length > 0 && (
                        <div style={{ marginBottom: 6 }}>
                            {pendingExports.map(exp => {
                                const isExpired = exp.status === 'pending' && (Date.now() - exp.startedAt) >= EXPORT_TIMEOUT_MS;
                                const statusClass = isExpired ? 'expired' : exp.status;
                                return (
                                    <div key={exp.id} className={`pending-export-item pending-export-item--${statusClass}`}>
                                        <div className="pending-export-item__header">
                                            {exp.status === 'pending' && !isExpired && (
                                                <Icon className="pending-export-spinner" style={{ fontSize: 16, color: '#1565c0' }}>sync</Icon>
                                            )}
                                            {exp.status === 'complete' && (
                                                <Icon style={{ fontSize: 16, color: '#2e7d32' }}>check_circle</Icon>
                                            )}
                                            {isExpired && (
                                                <Icon style={{ fontSize: 16, color: '#c62828' }}>error</Icon>
                                            )}
                                            <span>
                                                {isExpired
                                                    ? messages['menu_account_export_expired_label']
                                                    : exp.status === 'complete'
                                                        ? messages['menu_account_export_complete_label']
                                                        : messages['menu_account_export_pending_label']}
                                            </span>
                                        </div>
                                        <div className="pending-export-item__name">{exp.name}</div>
                                        {exp.status === 'pending' && !isExpired && (
                                            <div className="pending-export-item__elapsed">
                                                {messages['menu_account_export_elapsed']}: {formatElapsed(exp.startedAt)}
                                            </div>
                                        )}
                                        <div className="pending-export-item__actions">
                                            {exp.status === 'complete' && (
                                                <Button
                                                    size="small"
                                                    className="btn-dark-blue"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const url = exp.url;
                                                        const urlStr = Array.isArray(url) ? (url[0]?.url || '') : (url || '');
                                                        if (urlStr) {
                                                            const cleanUrl = urlStr.split('?')[0];
                                                            dispatch(downloadReportFileRequest({ id: cleanUrl }));
                                                        }
                                                        // Do NOT remove — user may need to retry
                                                    }}
                                                >
                                                    {messages['menu_account_export_download']}
                                                </Button>
                                            )}
                                            {exp.status === 'complete' && (
                                                <Tooltip title={copiedId === exp.id ? messages['menu_account_export_copied'] : messages['menu_account_export_copy_link']}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const url = exp.url;
                                                            const urlStr = Array.isArray(url) ? (url[0]?.url || '') : (url || '');
                                                            if (urlStr) {
                                                                // Copy the full signed URL (including query string) so S3 grants access
                                                                navigator.clipboard.writeText(urlStr);
                                                                setCopiedId(exp.id);
                                                                setTimeout(() => setCopiedId(null), 2000);
                                                            }
                                                        }}
                                                    >
                                                        <Icon style={{ fontSize: 16, color: copiedId === exp.id ? '#2e7d32' : '#555' }}>
                                                            {copiedId === exp.id ? 'check' : 'content_copy'}
                                                        </Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title={messages['menu_account_export_dismiss']}>
                                                <IconButton
                                                    size="small"
                                                    className="pending-export-trash"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        dispatch(removePendingExport(exp.id));
                                                    }}
                                                >
                                                    <Icon style={{ fontSize: 16, color: '#999' }}>delete</Icon>
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {/* ---- Persisted DB notifications ---- */}
                    <Table
                        ref={tableRef}
                        columns={[
                            {
                                key: "script",
                                title: messages['menu_account_notifications_title'],
                                width: 200,
                                show: true,
                                sort: false,
                                locked: false,
                                resize: false,
                                render: (item) => {

                                    return <>
                                        <Grid container alignItems="center" wrap="nowrap">
                                            {
                                                getNotification(item)
                                            }
                                            {
                                                !item.read &&
                                                <Grid item>
                                                    <Tooltip title={messages["menu_account_notifications_mark_as_read"]} placement="right">
                                                        <IconButton
                                                            onClick={(e) => {
                                                                //to mark as read
                                                                dispatch(updateNotificationRequest(item.id))
                                                            }}
                                                            aria-haspopup="true"
                                                            size="large">
                                                            <Icon style={{ color: 'blue' }}>adjustOutlined</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                            }
                                        </Grid>
                                    </>;
                                }
                            }
                        ]}
                        data={notifications}
                        height={innerHeight - 280}
                        infiniteScroll
                        nextToken={notificationsNextToken}
                        externalLoading={loadingNotifications || loadingUpdateNotification}
                        disableItemSelection
                        onChangePage={() => {
                            dispatch(searchNotificationsRequest(10, notificationsNextToken));
                        }}
                        messages={messages}
                        noMoreItemsMessage={notifications?.length > 0 ? '\u200B' : messages['no_more_items']}
                    />
                    </Box>
                </Grid>
            </Grid>
        </Menu>
    </>;
}

export default MenuAccount;